import { useState } from "react";
import { motion } from "framer-motion";
import { Send, Phone, Mail, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface PropertyContactFormProps {
  propertyTitle: string;
}

export const PropertyContactForm = ({ propertyTitle }: PropertyContactFormProps) => {
  const { toast } = useToast();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: `I am interested in ${propertyTitle}. Please provide more information.`,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      const { data, error } = await supabase.functions.invoke("submit-lead", {
        body: {
          name: formData.name,
          email: formData.email,
          phone: formData.phone || undefined,
          message: formData.message,
          property_interest: propertyTitle,
          source: "property_inquiry",
        },
      });

      if (error) throw error;

      toast({
        title: "Inquiry Sent",
        description: "Thank you for your interest. Our team will contact you within 24 hours.",
      });
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: `I am interested in ${propertyTitle}. Please provide more information.`,
      });
    } catch (error: any) {
      console.error("Error submitting form:", error);
      const errorMessage = error?.message?.includes("rate limit") 
        ? "Too many submissions. Please try again later."
        : "Failed to submit inquiry. Please try again.";
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6 }}
      className="bg-secondary/30 backdrop-blur-sm border border-border/30 p-8 lg:p-10"
    >
      <h3 className="font-display text-2xl lg:text-3xl text-foreground mb-2">
        Schedule a Viewing
      </h3>
      <p className="font-body text-muted-foreground mb-8">
        Request a private tour of this exceptional property
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <Input
            type="text"
            placeholder="Your Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            className="bg-background/50 border-border/50 focus:border-gold h-14 font-body"
          />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Input
            type="email"
            placeholder="Email Address"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            required
            className="bg-background/50 border-border/50 focus:border-gold h-14 font-body"
          />
          <Input
            type="tel"
            placeholder="Phone Number"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            className="bg-background/50 border-border/50 focus:border-gold h-14 font-body"
          />
        </div>
        <Textarea
          placeholder="Your Message"
          value={formData.message}
          onChange={(e) => setFormData({ ...formData, message: e.target.value })}
          required
          rows={4}
          className="bg-background/50 border-border/50 focus:border-gold font-body resize-none"
        />

        <Button 
          type="submit" 
          variant="luxury" 
          size="xl" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <>
              <Loader2 size={18} className="animate-spin" />
              Sending...
            </>
          ) : (
            <>
              Send Inquiry
              <Send size={18} />
            </>
          )}
        </Button>
      </form>

      {/* Quick Contact */}
      <div className="mt-8 pt-8 border-t border-border/30">
        <p className="font-body text-sm text-muted-foreground mb-4">Or contact directly:</p>
        <div className="space-y-3">
          <a
            href="tel:+37060044499"
            className="flex items-center gap-3 text-foreground hover:text-gold transition-colors font-body"
          >
            <Phone size={18} className="text-gold" />
            +370 600 44 499
          </a>
          <a
            href="mailto:ask@agotaestate.com"
            className="flex items-center gap-3 text-foreground hover:text-gold transition-colors font-body"
          >
            <Mail size={18} className="text-gold" />
            ask@agotaestate.com
          </a>
        </div>
      </div>
    </motion.div>
  );
};
